/**
 * Service for tarifs (subscription plans) API
 * Handles fetching tarifs by profile type and modules by subscription
 */


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Get tarifs available for a specific profile type
 * @param {number} profileTypeId - Profile type ID
 * @returns {Promise<Array>} List of tarifs with pricing
 */
export const getTarifsByProfileType = async (profileTypeId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tarifs/by-profile-type/${profileTypeId}`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching tarifs by profile type:', error);
        return [];
    }
};

/**
 * Get all tarifs (active only)
 * @returns {Promise<Array>} List of all active tarifs
 */
export const getAllTarifs = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/tarifs`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching all tarifs:', error);
        return [];
    }
};

/**
 * Get modules available for a profile type filtered by subscription type
 * @param {number} profileTypeId - Profile type ID
 * @param {string} subscriptionType - Subscription type (FREE, ARGENT, OR, etc.)
 * @returns {Promise<Array>} List of modules for this subscription level
 */
export const getModulesByProfileTypeAndSubscription = async (profileTypeId, subscriptionType = '') => {
    try {
        let url = `${API_BASE_URL}/profiletype_modules/by-profiletype/${profileTypeId}`;
        if (subscriptionType) {
            url += `?subscriptionType=${encodeURIComponent(subscriptionType)}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        // Filter only active modules
        return success ? data.filter(m => {
            const isActive = m.isActive ?? m.ISACTIVE ?? m.isactive ?? true;
            return isActive === true || isActive === 1;
        }) : [];
    } catch (error) {
        console.error('Error fetching modules by profile type and subscription:', error);
        return [];
    }
};

/**
 * Get user's profile type from localStorage or API
 * @returns {Object|null} Profile type info
 */
export const getUserProfileType = () => {
    const profileTypeId = localStorage.getItem('userProfileTypeId');
    const profileTypeCode = localStorage.getItem('profileTypeCode');

    return {
        id: profileTypeId ? parseInt(profileTypeId, 10) : null,
        code: profileTypeCode || null
    };
};

/**
 * Quels paliers font sauter les limites de CE module pour CE type de profil ?
 *
 * Pendant la bêta, tous les modules sont ouverts à tous : la question n'est
 * plus « qui y a accès » mais « quel abonnement déplafonne ». Et la réponse
 * dépend du type de profil — Bibliothèque est comprise dans STARTER pour un
 * particulier, seul FULL la couvre pour un artisan.
 *
 * Les tarifs arrivent triés par prix croissant : le premier est le moins
 * cher qui réponde réellement au besoin.
 *
 * @param {number} profileTypeId
 * @param {string} moduleCode
 * @returns {Promise<Array>} Tarifs qui déplafonnent, ou [] si aucun
 */
export const getTarifsUnlockingModule = async (profileTypeId, moduleCode) => {
    try {
        const response = await fetch(
            `${API_BASE_URL}/profiletype_modules/unlocking/${profileTypeId}/${encodeURIComponent(moduleCode)}`,
            { method: 'GET', credentials: 'include' }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result.DATA ?? result.data ?? [];
    } catch (error) {
        console.error('Error fetching unlocking tarifs:', error);
        // Rendre une liste vide plutôt que de propager : l'écran de limite
        // sait ne rien proposer, il ne doit pas échouer pour autant.
        return [];
    }
};

/**
 * Un tarif par son code.
 *
 * Sert le bloc `tarif` des pages de vente : le prix est LU EN BASE, jamais
 * saisi dans le bloc. Vise aussi bien une formule globale qu'une
 * application autonome.
 *
 * Rend null plutôt que de propager : le bloc sait n'afficher aucun prix, et
 * un prix faux serait pire qu'un prix absent.
 *
 * @param {string} code - Le code du tarif (FULL, BIBLIO_SOLO, ...)
 * @returns {Promise<object|null>}
 */
export const getTarifByCode = async (code) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tarifs/by-code/${encodeURIComponent(code)}`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) return null;

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;

        if (!success || !data) return null;
        return data;
    } catch (error) {
        console.error('Error fetching tarif by code:', error);
        return null;
    }
};


/**
 * Plafonds permanents du plan Gratuit, par module.
 * @returns {Promise<Array>} Liste de { modulecode, limitcode, limitvalue }, vide si illisible
 */
export const getFreeLimits = async () => {
    try {
        const resTarif = await fetch(`${API_BASE_URL}/tarifs/by-code/FREE`, { credentials: 'include' });
        const tarif = await resTarif.json();
        const dataTarif = tarif.data ?? tarif.DATA ?? {};
        const tarifId = dataTarif.id ?? dataTarif.ID;
        if (!tarifId) return [];

        const resLimites = await fetch(`${API_BASE_URL}/tarif_limits/by-tarif/${tarifId}`, { credentials: 'include' });
        const limites = await resLimites.json();
        const liste = limites.data ?? limites.DATA ?? [];
        return liste.filter(l => (l.isactive ?? l.ISACTIVE ?? 1) === 1 || (l.isactive ?? l.ISACTIVE) === true);
    } catch (error) {
        console.error('Error fetching free limits:', error);
        return [];
    }
};

/**
 * Nom d'un type de profil dans la langue donnée
 * @param {number} profileTypeId
 * @param {string} lang
 * @returns {Promise<string>} Nom, ou chaîne vide
 */
export const getProfileTypeLabel = async (profileTypeId, lang = 'fr') => {
    try {
        const res = await fetch(`${API_BASE_URL}/profiletypes`, { credentials: 'include' });
        const result = await res.json();
        const liste = result.data ?? result.DATA ?? [];
        const pt = liste.find(x => (x.id ?? x.ID) === profileTypeId);
        if (!pt) return '';
        return pt[`name_${lang}`] || pt.name_fr || pt.code || '';
    } catch (error) {
        console.error('Error fetching profile type label:', error);
        return '';
    }
};
